/**
 * Loss Reserve Screen
 *
 * Initial scaffolding for the loss-reserve analysis. Currently surfaces:
 *   - gross_sales (daily)
 *   - gross_sales_for_default_ratio (forward sum over default_horizon, default 120 days)
 *
 * Mirrors the dilution flow (`receivables_dilution`) but uses a longer horizon
 * tailored to loss/default recognition. Additional reserve columns will be added
 * incrementally.
 */
import { useState } from "react";
import { useDispatch } from "react-redux";
// -------------------------------------------------------------
import { calculateLoss, getLossData } from "Redux/data/actions";
import { useAnalysis, useScenarioFlag } from "Redux/data/hooks";
import DataTable from "Components/DataTable";
import SkeletonTable from "Components/Skeleton";
import CalcParamsModal, { ParamsButton } from "Components/CalcParamsModal";
import { LOSS_RESERVE_FIELDS } from "Config/calcParamsConfig";

const headers = [
  { text: "Reference Date", key: "Reference Date", datatype: "date" },
  { text: "Gross Sales", key: "gross_sales", datatype: "float" },
  { text: "Gross Sales for Default Ratio", key: "gross_sales_for_default_ratio", datatype: "float" },
  { text: "Gross Sales for Loss Horizon Ratio", key: "gross_sales_for_loss_horizon_ratio", datatype: "float" },
  { text: "NERB", key: "nerb", datatype: "float" },
  { text: "Deemed Default Proxy", key: "deemed_default_proxy", datatype: "float" },
  { text: "Write-offs", key: "write_offs", datatype: "float" },
  { text: "Loss Proxy", key: "loss_proxy", datatype: "float" },
  { text: "Default Ratio", key: "default_ratio", datatype: "float", display: "percentage" },
  { text: "Default Ratio 3 Mth Ave", key: "default_ratio_3mth_avg", datatype: "float", display: "percentage" },
  { text: "Loss Ratio", key: "loss_ratio", datatype: "float", display: "percentage" },
  { text: "Loss Horizon Ratio", key: "loss_horizon_ratio", datatype: "float" },
  { text: "Loss Reserve Dynamic %", key: "loss_reserve_dynamic", datatype: "float", display: "percentage" },
  { text: "Loss Reserve Floor", key: "loss_reserve_floor", datatype: "float", display: "percentage" },
];

const LossReserve = () => {
  const dispatch = useDispatch();
  const [calculating, setCalculating] = useState(false);
  const [showParams, setShowParams] = useState(false);

  const { data: loss, has: hasLoss } = useAnalysis("loss", getLossData);
  const hasMaster = useScenarioFlag("hasMaster");

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  return (
    <div>
      {showParams && (
        <CalcParamsModal
          section="lossReserve"
          title="Loss Reserve Parameters"
          fields={LOSS_RESERVE_FIELDS}
          onClose={() => setShowParams(false)}
        />
      )}
      <div className="box" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <button
          className={`button is-small ${calculating ? "is-loading" : ""}`}
          onClick={() => {
            setCalculating(true);
            dispatch(calculateLoss(() => setCalculating(false)));
          }}
        >
          {hasLoss ? "Recalculate" : "Calculate"} Loss Reserve
        </button>
        <ParamsButton onClick={() => setShowParams(true)} />
      </div>
      {hasLoss && !loss && <SkeletonTable />}
      {loss && (
        <DataTable
          data={loss.data}
          headers={headers}
          reference="Reference Date"
        />
      )}
    </div>
  );
};

export default LossReserve;
